import styled from '@emotion/styled';
import { css } from '@emotion/react';
import typography from 'storybookConfig/mixins/typography';
import materialSymbol from 'storybookConfig/mixins/materialSymbol';

interface InputChipProps {
  selected?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
  onClick?: () => void;
}

const getColor = ({
  disabled,
  selected,
  theme,
}: {
  disabled?: boolean;
  selected?: boolean;
  theme: any;
}) => {
  if (disabled) return theme.color.bodyTextDisabled;
  if (selected) return theme.color.bodyTextPrimary;
  return theme.color.bodyTextSecondary;
};

const ChipButton = styled.button<InputChipProps>`
  background-color: transparent;
  border: 1px solid;
  border-radius: 6px;
  padding: 8px 12px 8px 12px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  cursor: pointer;

  color: ${({ selected, theme }) =>
    selected ? theme.color.accent : theme.color.bodyTextSecondary};
  border-color: ${({ selected, theme }) =>
    selected ? theme.color.accent : theme.color.bodyTextSecondary};

  /* Iris 600 drops to 4.25:1 on the subtle fill, so selected text darkens to accentStrong. */
  &:hover {
    background-color: ${({ theme }) => theme.color.surfaceSubtle};
    ${({ selected, theme }) => selected && `color: ${theme.color.accentStrong};`}
  }

  ${({ disabled }) =>
    disabled &&
    css`
      cursor: not-allowed;
      opacity: 0.5;
    `}

  ${typography('Chips/Chip Text Large')};

  ${({ selected, disabled, theme }) =>
    materialSymbol({
      name: selected ? 'circle-x' : 'circle-plus',
      position: 'after',
      color: getColor({ disabled, selected, theme }),
      size: '20px',
    })}
`;

const InputChip = ({ children, selected, disabled, onClick }: InputChipProps) => (
  <ChipButton
    type="button"
    onClick={disabled ? undefined : onClick}
    selected={selected}
    disabled={disabled}
    aria-pressed={selected}
  >
    {children}
  </ChipButton>
);

export default InputChip;
